import { ApiResponse } from '@shared/types';
import {
  DashboardDepartmentPerformance,
  DashboardMetricSummary,
  DashboardOutletPerformance,
  DashboardOverview,
  DashboardPaymentBreakdown,
  DashboardTrendPoint,
} from '../types/dashboard';
import { SalesReportOrderView, SalesReportUserSummary } from '../types/report';
import { supabase } from './supabase';

const DAY_LABEL_FORMATTER = new Intl.DateTimeFormat('en-UG', {
  day: 'numeric',
  month: 'short',
});

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const parseDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (value: Date, days: number): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate() + days);

const buildDateRange = (startDate: string, endDate: string): string[] => {
  const values: string[] = [];
  let current = parseDate(startDate);
  const end = parseDate(endDate);

  while (current <= end) {
    values.push(formatDate(current));
    current = addDays(current, 1);
  }

  return values;
};

const mapUserSummary = (value: any): SalesReportUserSummary => ({
  id: value?.id ?? null,
  first_name: value?.first_name ?? null,
  last_name: value?.last_name ?? null,
  outlet_id: value?.outlet_id ?? null,
});

const mapOrder = (value: any): SalesReportOrderView => ({
  order_id: value.order_id,
  bill_number: toNumber(value.bill_number),
  sale_date: value.sale_date,
  outlet_id: value.outlet_id,
  company_id: toNumber(value.company_id),
  outlet_name: value.outlet_name || '',
  table_id: value.table_id === null ? null : toNumber(value.table_id),
  table_name: value.table_name ?? null,
  section_id: value.section_id === null ? null : toNumber(value.section_id),
  section_name: value.section_name ?? null,
  mode: value.mode,
  status_id: toNumber(value.status_id),
  status_name: value.status_name || '',
  client_id: value.client_id ?? null,
  client_name: value.client_name ?? null,
  waiter_id: value.waiter_id ?? null,
  waiter_name: value.waiter_name ?? null,
  item_count: toNumber(value.item_count),
  pending_item_count: toNumber(value.pending_item_count),
  discount_amount: toNumber(value.discount_amount),
  total_amount: toNumber(value.total_amount),
  net_amount: toNumber(value.net_amount),
  settlement_total: toNumber(value.settlement_total),
  settlement_count: toNumber(value.settlement_count),
  balance_amount: toNumber(value.balance_amount),
  is_fully_settled: Boolean(value.is_fully_settled),
  created_by: mapUserSummary(value.created_by),
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const applyScope = <T extends { eq: Function }>(
  query: T,
  companyId: number,
  outletId: string | null,
) => {
  if (outletId) {
    return query.eq('outlet_id', outletId);
  }

  return query.eq('company_id', companyId);
};

const aggregateSummary = (rows: any[]): DashboardMetricSummary => {
  const summary = rows.reduce<DashboardMetricSummary>(
    (acc, row) => {
      acc.grossSales += toNumber(row.gross_sale);
      acc.netSales += toNumber(row.total_sale);
      acc.totalDiscounts += toNumber(row.total_discount);
      acc.billsSettled += toNumber(row.settled_order_count);
      acc.settlementCount += toNumber(row.settlement_count);
      return acc;
    },
    {
      grossSales: 0,
      netSales: 0,
      totalDiscounts: 0,
      billsSettled: 0,
      settlementCount: 0,
      averageBill: 0,
      creditIssued: 0,
      ncIssued: 0,
    },
  );

  summary.averageBill =
    summary.billsSettled > 0 ? summary.netSales / summary.billsSettled : 0;

  return summary;
};

const aggregateTrend = (
  summaryRows: any[],
  startDate: string,
  endDate: string,
): DashboardTrendPoint[] => {
  const trendMap = new Map<string, DashboardTrendPoint>(
    buildDateRange(startDate, endDate).map((date) => [
      date,
      {
        sale_date: date,
        label: DAY_LABEL_FORMATTER.format(parseDate(date)),
        gross_sales: 0,
        net_sales: 0,
        discounts: 0,
        bills_settled: 0,
      },
    ]),
  );

  summaryRows.forEach((row) => {
    const existing = trendMap.get(row.sale_date);
    if (!existing) return;

    existing.gross_sales += toNumber(row.gross_sale);
    existing.net_sales += toNumber(row.total_sale);
    existing.discounts += toNumber(row.total_discount);
    existing.bills_settled += toNumber(row.settled_order_count);
  });

  return Array.from(trendMap.values());
};

const aggregatePayments = (paymentRows: any[]): DashboardPaymentBreakdown[] => {
  const group = new Map<string, DashboardPaymentBreakdown>();

  paymentRows.forEach((row) => {
    const key = row.payment_method_name || 'Unknown';
    const existing = group.get(key) || {
      payment_method_name: key,
      total_amount: 0,
      settlement_count: 0,
      percentage: 0,
    };

    existing.total_amount += toNumber(row.total_amount);
    existing.settlement_count += toNumber(row.settlement_count);
    group.set(key, existing);
  });

  const totalAmount = Array.from(group.values()).reduce(
    (acc, item) => acc + item.total_amount,
    0,
  );

  return Array.from(group.values())
    .map((item) => ({
      ...item,
      percentage: totalAmount > 0 ? (item.total_amount / totalAmount) * 100 : 0,
    }))
    .sort((left, right) => right.total_amount - left.total_amount);
};

const aggregateDepartments = (
  departmentRows: any[],
): DashboardDepartmentPerformance[] => {
  const group = new Map<string, DashboardDepartmentPerformance>();

  departmentRows.forEach((row) => {
    const key = row.department_name || 'Unassigned';
    const existing = group.get(key) || {
      department_name: key,
      total_sale: 0,
      gross_sale: 0,
      total_discount: 0,
      quantity_sold: 0,
      item_count: 0,
    };

    existing.total_sale += toNumber(row.total_sale);
    existing.gross_sale += toNumber(row.gross_sale);
    existing.total_discount += toNumber(row.total_discount);
    existing.quantity_sold += toNumber(row.quantity_sold);
    existing.item_count += toNumber(row.item_count);
    group.set(key, existing);
  });

  return Array.from(group.values())
    .sort((left, right) => right.total_sale - left.total_sale)
    .slice(0, 8);
};

const aggregateOutletPerformance = (
  summaryRows: any[],
): DashboardOutletPerformance[] => {
  const group = new Map<string, DashboardOutletPerformance>();

  summaryRows.forEach((row) => {
    const key = row.outlet_id;
    const existing = group.get(key) || {
      outlet_id: row.outlet_id,
      outlet_name: row.outlet_name || 'Outlet',
      net_sales: 0,
      gross_sales: 0,
      total_discounts: 0,
      bills_settled: 0,
      settlement_count: 0,
      average_bill: 0,
    };

    existing.net_sales += toNumber(row.total_sale);
    existing.gross_sales += toNumber(row.gross_sale);
    existing.total_discounts += toNumber(row.total_discount);
    existing.bills_settled += toNumber(row.settled_order_count);
    existing.settlement_count += toNumber(row.settlement_count);
    group.set(key, existing);
  });

  return Array.from(group.values())
    .map((item) => ({
      ...item,
      average_bill:
        item.bills_settled > 0 ? item.net_sales / item.bills_settled : 0,
    }))
    .sort((left, right) => right.net_sales - left.net_sales);
};

interface FetchParams {
  companyId: number;
  outletId: string | null;
  startDate: string;
  endDate: string;
}

export const fetchDashboardSummary = async ({
  companyId,
  outletId,
  startDate,
  endDate,
}: FetchParams): Promise<
  ApiResponse<
    Pick<
      DashboardOverview,
      'startDate' | 'endDate' | 'summary' | 'trend' | 'outletPerformance'
    >
  >
> => {
  try {
    const summaryResponse = await applyScope(
      supabase
        .from('sales_summary_view')
        .select('*')
        .gte('sale_date', startDate)
        .lte('sale_date', endDate)
        .order('sale_date', { ascending: true }),
      companyId,
      outletId,
    );
    if (summaryResponse.error) throw summaryResponse.error;

    const creditorsResponse = await applyScope(
      supabase
        .from('sales_creditor_settlements_view')
        .select('*')
        .gte('sale_date', startDate)
        .lte('sale_date', endDate),
      companyId,
      outletId,
    );
    if (creditorsResponse.error) throw creditorsResponse.error;

    const summaryRows = summaryResponse.data || [];
    const creditorRows = creditorsResponse.data || [];
    const summary = aggregateSummary(summaryRows);

    summary.creditIssued = creditorRows
      .filter(
        (row: any) =>
          String(row.payment_method_name || '').toUpperCase() === 'CREDIT',
      )
      .reduce((total: number, row: any) => total + toNumber(row.amount), 0);

    summary.ncIssued = creditorRows
      .filter(
        (row: any) =>
          String(row.payment_method_name || '').toUpperCase() === 'NC',
      )
      .reduce((total: number, row: any) => total + toNumber(row.amount), 0);

    return {
      success: true,
      data: {
        startDate,
        endDate,
        summary,
        trend: aggregateTrend(summaryRows, startDate, endDate),
        outletPerformance: aggregateOutletPerformance(summaryRows),
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch summary data',
    };
  }
};

export const fetchDashboardRecent = async ({
  companyId,
  outletId,
  startDate,
  endDate,
}: FetchParams): Promise<
  ApiResponse<Pick<DashboardOverview, 'recentOrders'>>
> => {
  try {
    const ordersResponse = await applyScope(
      supabase
        .from('sales_orders_view')
        .select('*')
        .gte('sale_date', startDate)
        .lte('sale_date', endDate)
        .order('created_at', { ascending: false })
        .limit(10),
      companyId,
      outletId,
    );
    if (ordersResponse.error) throw ordersResponse.error;

    return {
      success: true,
      data: {
        recentOrders: (ordersResponse.data || []).map(mapOrder),
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch recent data',
    };
  }
};

export const fetchDashboardPayments = async ({
  companyId,
  outletId,
  startDate,
  endDate,
}: FetchParams): Promise<
  ApiResponse<Pick<DashboardOverview, 'paymentBreakdown'>>
> => {
  try {
    const paymentResponse = await applyScope(
      supabase
        .from('payment_method_sales_summary_view')
        .select('*')
        .gte('sale_date', startDate)
        .lte('sale_date', endDate),
      companyId,
      outletId,
    );
    if (paymentResponse.error) throw paymentResponse.error;

    return {
      success: true,
      data: {
        paymentBreakdown: aggregatePayments(paymentResponse.data || []),
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch payment analytics',
    };
  }
};

export const fetchDashboardDepartments = async ({
  companyId,
  outletId,
  startDate,
  endDate,
}: FetchParams): Promise<
  ApiResponse<Pick<DashboardOverview, 'departmentPerformance'>>
> => {
  try {
    const departmentResponse = await applyScope(
      supabase
        .from('department_sales_summary_view')
        .select('*')
        .gte('sale_date', startDate)
        .lte('sale_date', endDate),
      companyId,
      outletId,
    );
    if (departmentResponse.error) throw departmentResponse.error;

    return {
      success: true,
      data: {
        departmentPerformance: aggregateDepartments(
          departmentResponse.data || [],
        ),
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch department analytics',
    };
  }
};
